import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, type ListParams } from "../lib/api";
import type { WorkItem, WorkKind } from "../lib/types";
import { Tile, SectionHeader, Pill } from "../components/primitives";
import { priorityVisual, channelChip } from "../lib/status";

const KIND_TABS: { label: string; value: WorkKind | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Tasks", value: "task" },
  { label: "Tickets", value: "ticket" },
];

// List view — a flat, filterable feed of work items with cursor pagination. Shares the same
// underlying list endpoint as Table; List is the readable stream, Table is the dense grid.
export function ListView() {
  const [kind, setKind] = useState<WorkKind | undefined>(undefined);
  const [q, setQ] = useState("");
  const params: ListParams = { kind, q: q || undefined, limit: 50 };
  const { data, isLoading } = useQuery({ queryKey: ["work-items", params], queryFn: () => api.listWorkItems(params) });

  return (
    <Tile>
      <SectionHeader title="List">
        <Pill className="pill-neutral">{data?.items.length ?? 0}</Pill>
      </SectionHeader>
      <FilterBar kind={kind} setKind={setKind} q={q} setQ={setQ} />
      <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 8 }}>
        {isLoading && <div style={{ padding: 12, color: "var(--text-3)" }}>Loading…</div>}
        {data?.items.map((it) => <ListRow key={it.id} item={it} />)}
        {data && data.items.length === 0 && <div style={{ padding: 12, color: "var(--text-3)" }}>No matching items.</div>}
      </div>
    </Tile>
  );
}

function ListRow({ item }: { item: WorkItem }) {
  const pv = priorityVisual(item.priority);
  const chip = channelChip(item.source);
  return (
    <Link
      to={`/item/${item.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 4px",
        borderBottom: "1px solid var(--border)",
        color: "inherit",
        textDecoration: "none",
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-4)", letterSpacing: 0.5, minWidth: 66 }}>{item.ref}</span>
      <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{item.title}</span>
      {chip && <span className={`chip ${chip.cls}`}>{chip.label}</span>}
      <span className={`pill ${pv.pillClass}`}>{pv.label}</span>
      <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600, minWidth: 70, textAlign: "right" }}>{item.status}</span>
    </Link>
  );
}

// Table view — the dense operational grid (spec). Same query, table-shaped, columnar.
export function TableView() {
  const [kind, setKind] = useState<WorkKind | undefined>(undefined);
  const [q, setQ] = useState("");
  const params: ListParams = { kind, q: q || undefined, limit: 100 };
  const { data, isLoading } = useQuery({ queryKey: ["table", params], queryFn: () => api.tableWorkItems(params) });

  return (
    <Tile>
      <SectionHeader title="Table">
        <Pill className="pill-neutral">{data?.items.length ?? 0}</Pill>
      </SectionHeader>
      <FilterBar kind={kind} setKind={setKind} q={q} setQ={setQ} />
      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--text-3)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {["Ref", "Title", "Status", "Priority", "Source", "Due"].map((h) => (
                <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 12, color: "var(--text-3)" }}>
                  Loading…
                </td>
              </tr>
            )}
            {data?.items.map((it) => {
              const pv = priorityVisual(it.priority);
              return (
                <tr key={it.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "9px 10px", fontWeight: 700, color: "var(--text-4)", whiteSpace: "nowrap" }}>
                    <Link to={`/item/${it.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                      {it.ref}
                    </Link>
                  </td>
                  <td style={{ padding: "9px 10px" }}>
                    <Link to={`/item/${it.id}`} style={{ color: "var(--text-1)", textDecoration: "none", fontWeight: 500 }}>
                      {it.title}
                    </Link>
                  </td>
                  <td style={{ padding: "9px 10px", color: "var(--text-2)" }}>{it.status}</td>
                  <td style={{ padding: "9px 10px" }}>
                    <span className={`pill ${pv.pillClass}`}>{pv.label}</span>
                  </td>
                  <td style={{ padding: "9px 10px", color: "var(--text-3)" }}>{it.source}</td>
                  <td style={{ padding: "9px 10px", color: "var(--text-3)", whiteSpace: "nowrap" }}>
                    {it.due_at ? new Date(it.due_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Tile>
  );
}

function FilterBar({
  kind,
  setKind,
  q,
  setQ,
}: {
  kind: WorkKind | undefined;
  setKind: (k: WorkKind | undefined) => void;
  q: string;
  setQ: (s: string) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
      {KIND_TABS.map((t) => (
        <button
          key={t.label}
          onClick={() => setKind(t.value)}
          className={`nav ${kind === t.value ? "on" : ""}`}
          style={{ padding: "6px 12px", fontSize: 12 }}
        >
          {t.label}
        </button>
      ))}
      <label style={{ marginLeft: "auto", display: "flex" }}>
        <span className="sr-only">Filter by title</span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter title…"
          style={{
            background: "var(--bg-2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "7px 12px",
            fontSize: 12.5,
            color: "var(--text-2)",
            fontFamily: "inherit",
          }}
        />
      </label>
    </div>
  );
}
